import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Paper,
  Container,
  Typography,
  IconButton,
  CircularProgress,
  Chip,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  Checkbox,
  FormControlLabel,
  Divider,
  Radio,
  RadioGroup,
} from "@mui/material";
import {
  ArrowBack,
  Edit,
  Close,
  DeleteOutline,
  Add,
  Lock,
  Category as CategoryIcon,
  InfoOutlined,
  ArrowUpward,
  ArrowDownward,
  Visibility,
} from "@mui/icons-material";
import HeaderInternal from "../components/Header/HeaderInternal";
import { FooterInternal } from "../components/Footer";
import { FaqButton } from "../components/FaqButton";
import ConfirmModal from "../components/modals/ConfirmModal";
import SavingOverlay from "../components/SavingOverlay";
import { Toast } from "../components/Toast";
import { useToast } from "../hooks/useToast";
import { useNavigation, useRouter } from "../contexts/RouterContext";
import { useAuth } from "../contexts/AuthContext";
import { colors, typography } from "../theme/designSystem";
import {
  assessmentFormsService,
  AssessmentPaginationMode,
  AssessmentFormType,
  assessmentErrorMessage,
  AssessmentFormEditor as EditorPayload,
  AssessmentSection,
  AssessmentQuestion,
  QUESTION_TYPE_LABELS,
  OptionInput,
} from "../services/assessmentFormsService";

const NONE = "";

interface SectionDraft {
  id: string | null;
  title: string;
  description: string;
  interpretationGuide: string;
  parentSectionId: string;
  ordenation: string;
  calcTypeId: string;
}

interface OptionDraft {
  id: string | null;
  label: string;
  value: string;
}

interface QuestionDraft {
  id: string | null;
  sectionId: string;
  text: string;
  description: string;
  questionType: string;
  required: boolean;
  allowComment: boolean;
  commentRequired: boolean;
  minChoices: string;
  maxChoices: string;
  ordenation: string;
  options: OptionDraft[];
}

const isChoice = (type: string) => type === "single_choice" || type === "multiple_choice";

// Builder de formulários de avaliação v2 (página-filha de assessment-forms; ?formId=).
// Estilo Google Forms: seções (2 níveis, pelo ícone no topo), itens com opções
// valoradas inline e instruções de interpretação (geral e por seção). Formulário aplicado
// (locked) tem estrutura imutável — duplicar/Nova versão na lista para evoluir.
const AssessmentFormEditor: React.FC = () => {
  const { user } = useAuth();
  const { goToDashboard } = useNavigation();
  const { navigateTo } = useRouter();
  const { toast, showToast, hideToast } = useToast();

  const formId = useMemo(
    () => new URLSearchParams(window.location.search).get("formId") ?? "",
    []
  );

  const [editor, setEditor] = useState<EditorPayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Cabeçalho
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [interpretationGuide, setInterpretationGuide] = useState("");
  const [calcTypeId, setCalcTypeId] = useState(NONE);
  const [allowPartial, setAllowPartial] = useState(false);
  const [paginationMode, setPaginationMode] = useState<AssessmentPaginationMode>("all");
  const [formTypeId, setFormTypeId] = useState<string>("");
  const [formTypes, setFormTypes] = useState<AssessmentFormType[]>([]);

  // Modais
  const [sectionsOpen, setSectionsOpen] = useState(false);
  const [sectionFilter, setSectionFilter] = useState<"all" | "root" | "child">("all");
  const [sectionDraft, setSectionDraft] = useState<SectionDraft | null>(null);
  const [questionDraft, setQuestionDraft] = useState<QuestionDraft | null>(null);
  const [sectionToDelete, setSectionToDelete] = useState<AssessmentSection | null>(null);
  const [questionToDelete, setQuestionToDelete] = useState<AssessmentQuestion | null>(null);
  const [questionSectionFilter, setQuestionSectionFilter] = useState(NONE);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const payload = await assessmentFormsService.getEditor(formId);
      setEditor(payload);
      setTitle(payload.form.title);
      setDescription(payload.form.description ?? "");
      setInterpretationGuide(payload.form.interpretationGuide ?? "");
      setCalcTypeId(payload.form.calcTypeId ?? NONE);
      setAllowPartial(payload.form.allowPartialCompletion);
      setPaginationMode(payload.form.paginationMode ?? "all");
      setFormTypeId(payload.form.formTypeId ?? "");
      assessmentFormsService.listTypes().then(setFormTypes).catch(() => setFormTypes([]));
    } catch (e) {
      showToast(assessmentErrorMessage(e, "Erro ao carregar o formulário."), "error");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formId]);

  useEffect(() => {
    if (formId) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formId]);

  const locked = editor?.form.locked ?? false;

  const sectionsById = useMemo(() => {
    const map = new Map<string, AssessmentSection>();
    editor?.sections.forEach((s) => map.set(s.id, s));
    return map;
  }, [editor]);

  const rootSections = useMemo(
    () => (editor?.sections ?? []).filter((s) => !s.parentSectionId),
    [editor]
  );

  const orderedSections = useMemo(() => {
    const result: AssessmentSection[] = [];
    rootSections.forEach((root) => {
      result.push(root);
      (editor?.sections ?? [])
        .filter((s) => s.parentSectionId === root.id)
        .forEach((child) => result.push(child));
    });
    (editor?.sections ?? [])
      .filter((s) => s.parentSectionId && !sectionsById.has(s.parentSectionId))
      .forEach((s) => result.push(s));
    return result;
  }, [editor, rootSections, sectionsById]);

  const sectionLabel = useCallback(
    (id: string | null): string => {
      if (!id) return "Sem seção";
      const section = sectionsById.get(id);
      if (!section) return "Sem seção";
      const parent = section.parentSectionId ? sectionsById.get(section.parentSectionId) : null;
      return parent ? `${parent.title} › ${section.title}` : section.title;
    },
    [sectionsById]
  );

  const calcTypeName = useCallback(
    (id: string | null): string | null =>
      id ? editor?.catalogs.calcTypes.find((c) => c.id === id)?.name ?? null : null,
    [editor]
  );

  const modalSections = useMemo(() => {
    if (sectionFilter === "root") return orderedSections.filter((s) => !s.parentSectionId);
    if (sectionFilter === "child") return orderedSections.filter((s) => s.parentSectionId);
    return orderedSections;
  }, [orderedSections, sectionFilter]);

  const visibleQuestions = useMemo(() => {
    const questions = editor?.questions ?? [];
    if (questionSectionFilter === NONE) return questions;
    return questions.filter((q) => q.sectionId === questionSectionFilter);
  }, [editor, questionSectionFilter]);

  const emptySectionDraft = (): SectionDraft => ({
    id: null,
    title: "",
    description: "",
    interpretationGuide: "",
    parentSectionId: NONE,
    ordenation: String((editor?.sections.length ?? 0) + 1),
    calcTypeId: NONE,
  });

  const emptyQuestionDraft = (): QuestionDraft => ({
    id: null,
    sectionId: questionSectionFilter,
    text: "",
    description: "",
    questionType: "single_choice",
    required: true,
    allowComment: false,
    commentRequired: false,
    minChoices: "",
    maxChoices: "",
    ordenation: String((editor?.questions.length ?? 0) + 1),
    options: [
      { id: null, label: "", value: "" },
      { id: null, label: "", value: "" },
    ],
  });

  const run = async (action: () => Promise<void>, successMessage: string, fallback: string) => {
    setSaving(true);
    try {
      await action();
      showToast(successMessage, "success");
      await load();
      return true;
    } catch (e) {
      showToast(assessmentErrorMessage(e, fallback), "error");
      return false;
    } finally {
      setSaving(false);
    }
  };

  const saveHeader = (activeOverride?: boolean) =>
    run(
      () =>
        assessmentFormsService.update(formId, {
          title,
          description: description.trim() || null,
          interpretationGuide: interpretationGuide.trim() || null,
          allowPartialCompletion: allowPartial,
          paginationMode,
          formTypeId,
          calcTypeId: calcTypeId || null,
          active: activeOverride ?? editor?.form.active ?? true,
        }),
      "Formulário salvo.",
      "Erro ao salvar o formulário."
    );

  const saveSection = async () => {
    if (!sectionDraft) return;
    const payload = {
      formId,
      title: sectionDraft.title,
      description: sectionDraft.description.trim() || null,
      interpretationGuide: sectionDraft.interpretationGuide.trim() || null,
      parentSectionId: sectionDraft.parentSectionId || null,
      ordenation: Number(sectionDraft.ordenation) || 0,
      calcTypeId: sectionDraft.calcTypeId || null,
    };
    const ok = await run(
      () =>
        sectionDraft.id
          ? assessmentFormsService.updateSection(sectionDraft.id, payload)
          : assessmentFormsService.createSection(payload).then(() => undefined),
      "Seção salva.",
      "Erro ao salvar a seção."
    );
    // Cadastro em sequência: o modal fica aberto e o form limpa
    if (ok) setSectionDraft(emptySectionDraft());
  };

  const saveQuestion = async () => {
    if (!questionDraft) return;
    const options: OptionInput[] = isChoice(questionDraft.questionType)
      ? questionDraft.options
          .filter((o) => o.label.trim().length > 0)
          .map((o) => ({
            id: o.id,
            label: o.label.trim(),
            value: o.value === "" ? null : Number(o.value),
          }))
      : [];
    const payload = {
      formId,
      sectionId: questionDraft.sectionId || null,
      text: questionDraft.text,
      description: questionDraft.description.trim() || null,
      questionType: questionDraft.questionType,
      required: questionDraft.required,
      allowComment: questionDraft.allowComment,
      commentRequired: questionDraft.allowComment && questionDraft.commentRequired,
      minChoices: questionDraft.minChoices === "" ? null : Number(questionDraft.minChoices),
      maxChoices: questionDraft.maxChoices === "" ? null : Number(questionDraft.maxChoices),
      ordenation: Number(questionDraft.ordenation) || 0,
      options,
    };
    const ok = await run(
      () =>
        questionDraft.id
          ? assessmentFormsService.updateQuestion(questionDraft.id, payload)
          : assessmentFormsService.createQuestion(payload).then(() => undefined),
      "Item salva.",
      "Erro ao salvar o item."
    );
    if (ok) setQuestionDraft(null);
  };

  const applyOptionsToSection = async () => {
    if (!questionDraft?.id) return;
    setSaving(true);
    try {
      const affected = await assessmentFormsService.applyOptionsToSection(questionDraft.id);
      showToast(`Opções aplicadas a ${affected} item${affected === 1 ? "" : "s"} da seção.`, "success");
      await load();
    } catch (e) {
      showToast(assessmentErrorMessage(e, "Erro ao aplicar as opções."), "error");
    } finally {
      setSaving(false);
    }
  };

  const moveOption = (index: number, delta: number) => {
    if (!questionDraft) return;
    const target = index + delta;
    if (target < 0 || target >= questionDraft.options.length) return;
    const options = [...questionDraft.options];
    [options[index], options[target]] = [options[target], options[index]];
    setQuestionDraft({ ...questionDraft, options });
  };

  const optionsSummary = (question: AssessmentQuestion): string =>
    question.options
      .map((o) => (o.value !== null ? `${o.label}=${o.value}` : o.label))
      .join(" · ");

  const sectionPaper = { p: 1.5, mb: 2, border: "1px solid #e9ecef", borderRadius: "8px" } as const;
  const fieldSx = { backgroundColor: "#fff" } as const;
  const topActionSx = {
    backgroundColor: colors.primary,
    color: "white",
    width: 40,
    height: 40,
    borderRadius: "6px",
    transition: "all 0.2s ease",
    "&:hover": { backgroundColor: colors.primaryHover },
  } as const;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}>
      <HeaderInternal
        showCTAButton={false}
        className="login-header"
        isLoggedIn={true}
        userEmail={user?.email || ""}
        userProfile={user?.role || ""}
        clinicName={user?.clinicName || "Clínica"}
        notificationCount={0}
        onRevalidateLogin={() => {}}
        onNotificationClick={() => {}}
        onUserClick={() => {}}
        onLogoClick={goToDashboard}
      />

      <Box component="main" sx={{ padding: "1rem", minHeight: "calc(100vh - 120px)", background: colors.background, marginTop: "85px", flex: 1 }}>
        <Container maxWidth={false} disableGutters>
          <Box sx={{ display: "flex", flexWrap: "wrap", justifyContent: "space-between", alignItems: "flex-start", mb: 1, gap: 2 }}>
            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
              <Tooltip title="Voltar para a lista" arrow>
                <IconButton size="small" onClick={() => navigateTo("assessment-forms")} sx={{ mt: 0.25, color: colors.primary }}>
                  <ArrowBack />
                </IconButton>
              </Tooltip>
              <Box>
                <Typography variant="h4" className="c4u-page-header__title">{editor ? editor.form.title : "Editor de formulário"}
                </Typography>
                <Typography variant="body2" className="c4u-page-header__subtitle">Seções (2 níveis), itens com opções de resposta e instruções de interpretação.
                </Typography>
              </Box>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              {editor && (
                <Chip label={`v${editor.form.version}`} size="small" sx={{ height: 20, fontSize: "0.7rem", fontWeight: 700, backgroundColor: "#e9ecef", color: colors.textSecondary }} />
              )}
              {editor && (
                <Chip
                  label={editor.form.active ? "Ativo" : "Inativo"}
                  size="small"
                  onClick={() => saveHeader(!editor.form.active)}
                  title={editor.form.active ? "Clique para inativar" : "Clique para ativar"}
                  sx={{ color: "white", fontWeight: 600, cursor: "pointer", backgroundColor: editor.form.active ? "#28a745" : "#adb5bd" }}
                />
              )}
              {editor && (
                <Tooltip title={`Seções do formulário (${editor.sections.length})`} arrow>
                  <IconButton
                    onClick={() => { setSectionDraft(emptySectionDraft()); setSectionFilter("all"); setSectionsOpen(true); }}
                    sx={topActionSx}
                  >
                    <CategoryIcon />
                  </IconButton>
                </Tooltip>
              )}
              <FaqButton />
            </Box>
          </Box>

          {loading || !editor ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
              <CircularProgress sx={{ color: colors.primary }} />
            </Box>
          ) : (
            <>
              {locked && (
                <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1, backgroundColor: "#fff5e0", border: "1px solid #ffe2b0", borderRadius: "8px", p: 1.25, mb: 2 }}>
                  <Lock sx={{ color: "#d98c00", fontSize: "1.1rem", mt: "2px" }} />
                  <Typography variant="body2" sx={{ color: "#8a5a00" }}>
                    Formulário aplicado {editor.form.applicationsCount}x a pacientes: a estrutura (seções, itens, opções e faixas) é imutável para preservar as avaliações registradas.
                    Título, descrição e status seguem editáveis. Para evoluir o instrumento, use <strong>Duplicar</strong> na lista de formulários.
                  </Typography>
                </Box>
              )}

              {/* Dados do formulário */}
              <Paper elevation={0} sx={sectionPaper}>
                <Typography sx={{ fontWeight: 600, color: colors.textPrimary, mb: 1.5 }}>Dados do formulário</Typography>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5 }}>
                  <TextField size="small" label="Título *" value={title} onChange={(e) => setTitle(e.target.value)}
                    InputLabelProps={{ shrink: true }} inputProps={{ maxLength: 255 }} sx={{ ...fieldSx, flex: "1 1 260px" }} />
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flex: "0 1 280px", minWidth: 230 }}>
                    <FormControl size="small" sx={{ flex: 1 }} disabled={locked}>
                      <InputLabel shrink>Tipo de resultado geral</InputLabel>
                      <Select label="Tipo de resultado geral" value={calcTypeId} onChange={(e) => setCalcTypeId(e.target.value)} notched displayEmpty>
                        <MenuItem value={NONE}>Nenhum</MenuItem>
                        {editor.catalogs.calcTypes.map((c) => (
                          <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <Tooltip title={editor.catalogs.calcTypes.find((c) => c.id === calcTypeId)?.description ?? "Como o resultado geral do formulário é calculado a partir dos valores das respostas."} arrow>
                      <InfoOutlined sx={{ fontSize: "1.1rem", color: colors.textSecondary, cursor: "help", flexShrink: 0 }} />
                    </Tooltip>
                  </Box>
                </Box>
                <TextField size="small" label="Descrição" value={description} onChange={(e) => setDescription(e.target.value)}
                  InputLabelProps={{ shrink: true }} inputProps={{ maxLength: 1000 }} multiline minRows={2} fullWidth sx={{ ...fieldSx, mt: 1.5 }} />
                <TextField size="small" label="Instruções de interpretação do resultado" value={interpretationGuide} onChange={(e) => setInterpretationGuide(e.target.value)}
                  placeholder="O 'manual' do instrumento: como o profissional deve ler o resultado. Aparece na tela de resultado, junto do número/faixa e do parecer dele."
                  InputLabelProps={{ shrink: true }} inputProps={{ maxLength: 4000 }} multiline minRows={2} fullWidth sx={{ ...fieldSx, mt: 1.5 }} />
                <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1, mt: 1.5 }}>
                  <FormControl size="small" sx={{ flex: "1 1 260px", maxWidth: 340 }}>
                    <InputLabel shrink>Tipo do formulário</InputLabel>
                    <Select
                      label="Tipo do formulário"
                      value={formTypeId}
                      onChange={(e) => setFormTypeId(e.target.value)}
                      notched
                      displayEmpty
                    >
                      <MenuItem value="">Sem tipo</MenuItem>
                      {formTypes.map((tipo) => (
                        <MenuItem key={tipo.id} value={tipo.id}>
                          {tipo.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Tooltip title="Para que o instrumento serve. É o tipo que faz o formulário contar nos relatórios: a anamnese marca a entrada do paciente e a avaliação periódica alimenta o ciclo de reavaliação. Sem tipo, o formulário funciona normalmente mas fica de fora dessas contas." arrow>
                    <InfoOutlined sx={{ fontSize: "1rem", color: colors.textSecondary }} />
                  </Tooltip>
                </Box>
                <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between", gap: 1, mt: 1.5 }}>
                  <FormControl size="small" sx={{ flex: "1 1 260px", maxWidth: 340 }}>
                    <InputLabel shrink>Apresentação ao profissional</InputLabel>
                    <Select
                      label="Apresentação ao profissional"
                      value={paginationMode}
                      onChange={(e) => setPaginationMode(e.target.value as AssessmentPaginationMode)}
                      notched
                    >
                      <MenuItem value="all">Mostrar tudo em uma página</MenuItem>
                      <MenuItem value="section">Uma seção por vez</MenuItem>
                    </Select>
                  </FormControl>
                  <Tooltip title="Instrumento longo cansa menos quando apresentado por seção; o índice lateral navega nos dois modos. Pode ser trocado a qualquer momento, inclusive com o formulário travado." arrow>
                    <InfoOutlined sx={{ fontSize: "1rem", color: colors.textSecondary }} />
                  </Tooltip>
                </Box>
                <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between", gap: 1, mt: 1 }}>
                  <FormControlLabel
                    control={<Checkbox size="small" checked={allowPartial} disabled={locked} onChange={(e) => setAllowPartial(e.target.checked)} />}
                    label={<Typography variant="body2">Pode ser finalizado mesmo sem 100% preenchido</Typography>}
                  />
                  <Button variant="contained" size="small" disabled={!title.trim() || saving} onClick={() => saveHeader()} sx={{ textTransform: "none", backgroundColor: colors.primary }}>
                    Salvar dados
                  </Button>
                </Box>
              </Paper>

              {/* Itens */}
              <Paper elevation={0} sx={sectionPaper}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 1, gap: 1, flexWrap: "wrap" }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
                    <Typography sx={{ fontWeight: 600, color: colors.textPrimary }}>
                      Itens ({visibleQuestions.length}{questionSectionFilter !== NONE ? ` de ${editor.questions.length}` : ""})
                    </Typography>
                    <FormControl size="small" sx={{ minWidth: 200 }}>
                      <InputLabel shrink>Seção</InputLabel>
                      <Select label="Seção" value={questionSectionFilter} onChange={(e) => setQuestionSectionFilter(e.target.value)} notched displayEmpty>
                        <MenuItem value={NONE}>Todas</MenuItem>
                        {orderedSections.map((s) => (
                          <MenuItem key={s.id} value={s.id}>{sectionLabel(s.id)}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>
                  {!locked && (
                    <Button size="small" startIcon={<Add />} onClick={() => setQuestionDraft(emptyQuestionDraft())} sx={{ color: colors.primary }}>
                      Novo item
                    </Button>
                  )}
                </Box>
                {visibleQuestions.length === 0 ? (
                  <Typography variant="body2" sx={{ color: colors.textSecondary }}>
                    Nenhumo item{questionSectionFilter !== NONE ? " nesta seção" : ""}. Itens de escolha carregam as próprias opções de resposta, com valor para pontuação.
                  </Typography>
                ) : (
                  <Box sx={{ display: "flex", flexDirection: "column" }}>
                    {visibleQuestions.map((question, index) => (
                      <Box key={question.id}>
                        {index > 0 && <Divider />}
                        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1, py: 0.75 }}>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography sx={{ color: colors.textPrimary, wordBreak: "break-word", lineHeight: 1.35 }}>
                              {question.ordenation}. {question.text}
                            </Typography>
                            <Typography variant="caption" sx={{ color: colors.textSecondary, display: "block" }}>
                              {sectionLabel(question.sectionId)} · {QUESTION_TYPE_LABELS[question.questionType] ?? question.questionType}
                              {question.required ? " · Obrigatória" : ""}
                              {question.allowComment ? ` · Comentário${question.commentRequired ? " obrigatório" : ""}` : ""}
                              {question.questionType === "multiple_choice" && (question.minChoices !== null || question.maxChoices !== null)
                                ? ` · ${question.minChoices ?? "–"} a ${question.maxChoices ?? "–"} escolhas` : ""}
                            </Typography>
                            {question.options.length > 0 && (
                              <Typography variant="caption" sx={{ color: colors.textMuted, display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                {optionsSummary(question)}
                              </Typography>
                            )}
                          </Box>
                          <Box sx={{ display: "flex", gap: 0.5, flexShrink: 0 }}>
                            <Tooltip title={locked ? "Visualizar item e opções de resposta" : "Editar item"}>
                              <IconButton size="small" onClick={() => setQuestionDraft({ id: question.id, sectionId: question.sectionId ?? NONE, text: question.text, description: question.description ?? "", questionType: question.questionType, required: question.required, allowComment: question.allowComment, commentRequired: question.commentRequired, minChoices: question.minChoices?.toString() ?? "", maxChoices: question.maxChoices?.toString() ?? "", ordenation: String(question.ordenation), options: question.options.map((o) => ({ id: o.id, label: o.label, value: o.value?.toString() ?? "" })) })} sx={{ color: colors.primary }}>
                                {locked ? <Visibility sx={{ fontSize: "1rem" }} /> : <Edit sx={{ fontSize: "1rem" }} />}
                              </IconButton>
                            </Tooltip>
                            {!locked && (
                              <IconButton size="small" onClick={() => setQuestionToDelete(question)} sx={{ color: colors.textSecondary, "&:hover": { color: "#dc3545" } }}>
                                <DeleteOutline sx={{ fontSize: "1rem" }} />
                              </IconButton>
                            )}
                          </Box>
                        </Box>
                      </Box>
                    ))}
                  </Box>
                )}
              </Paper>
            </>
          )}
        </Container>
      </Box>

      {/* Modal de seções (ícone no topo): lista + formulário, como no padrão aprovado */}
      <Dialog open={sectionsOpen} onClose={() => setSectionsOpen(false)} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: "12px", position: "relative", overflow: "hidden" } }}>
        <SavingOverlay open={saving} message="Salvando..." />
        <DialogTitle sx={{ backgroundColor: colors.primary, color: colors.white, display: "flex", justifyContent: "space-between", alignItems: "center", padding: "1rem 1.25rem" }}>
          <Typography component="h3" sx={{ fontSize: "1.1rem", fontWeight: typography.fontWeight.semibold, margin: 0 }}>
            Seções do formulário ({editor?.sections.length ?? 0})
          </Typography>
          <IconButton onClick={() => setSectionsOpen(false)} sx={{ color: colors.white, padding: "0.25rem" }}><Close /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ padding: "1.25rem !important", paddingTop: "1.5rem !important" }}>
          <Box sx={{ display: "flex", gap: 2.5, flexWrap: "wrap" }}>
            {!locked && sectionDraft && (
              <Box sx={{ flex: "1 1 300px", minWidth: 260, display: "flex", flexDirection: "column", gap: 2 }}>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
                  <Typography sx={{ fontWeight: 600, color: colors.textPrimary }}>
                    {sectionDraft.id ? "Editar seção" : "Nova seção"}
                  </Typography>
                  {sectionDraft.id && (
                    <Button size="small" onClick={() => setSectionDraft(emptySectionDraft())} sx={{ textTransform: "none", color: "#6c757d" }}>
                      Cancelar edição
                    </Button>
                  )}
                </Box>
                <TextField label="Título *" size="small" value={sectionDraft.title} onChange={(e) => setSectionDraft({ ...sectionDraft, title: e.target.value })} InputLabelProps={{ shrink: true }} inputProps={{ maxLength: 255 }} fullWidth />
                <TextField label="Descrição" size="small" value={sectionDraft.description} onChange={(e) => setSectionDraft({ ...sectionDraft, description: e.target.value })} InputLabelProps={{ shrink: true }} inputProps={{ maxLength: 1000 }} multiline minRows={2} fullWidth />
                <TextField label="Instruções de interpretação do resultado da seção" size="small" value={sectionDraft.interpretationGuide} onChange={(e) => setSectionDraft({ ...sectionDraft, interpretationGuide: e.target.value })} placeholder="Como ler o resultado DESTA seção (exibido ao profissional na tela de resultado)." InputLabelProps={{ shrink: true }} inputProps={{ maxLength: 4000 }} multiline minRows={2} fullWidth />
                <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
                  <FormControl size="small" sx={{ flex: "1 1 180px" }}>
                    <InputLabel shrink>Seção mãe</InputLabel>
                    <Select label="Seção mãe" value={sectionDraft.parentSectionId} onChange={(e) => setSectionDraft({ ...sectionDraft, parentSectionId: e.target.value })} notched displayEmpty>
                      <MenuItem value={NONE}>Nenhuma (nível 1)</MenuItem>
                      {rootSections.filter((s) => s.id !== sectionDraft.id).map((s) => (
                        <MenuItem key={s.id} value={s.id}>{s.title}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <TextField label="Ordenação" size="small" type="number" value={sectionDraft.ordenation} onChange={(e) => setSectionDraft({ ...sectionDraft, ordenation: e.target.value })} InputLabelProps={{ shrink: true }} inputProps={{ min: 0, max: 9999 }} sx={{ flex: "0 1 110px" }} />
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                  <FormControl size="small" sx={{ flex: 1 }}>
                    <InputLabel shrink>Tipo de resultado da seção</InputLabel>
                    <Select label="Tipo de resultado da seção" value={sectionDraft.calcTypeId} onChange={(e) => setSectionDraft({ ...sectionDraft, calcTypeId: e.target.value })} notched displayEmpty>
                      <MenuItem value={NONE}>Nenhum</MenuItem>
                      {editor?.catalogs.calcTypes.map((c) => (
                        <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Tooltip title="Como o resultado DESTA seção é calculado a partir das respostas dela (ex.: soma dos valores comparada às faixas da seção)." arrow>
                    <InfoOutlined sx={{ fontSize: "1.1rem", color: colors.textSecondary, cursor: "help", flexShrink: 0 }} />
                  </Tooltip>
                </Box>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                  <span />
                  <Button variant="contained" disabled={!sectionDraft.title.trim() || saving} onClick={saveSection} sx={{ textTransform: "none", backgroundColor: colors.primary }}>
                    {sectionDraft.id ? "Salvar alterações" : "Incluir seção"}
                  </Button>
                </Box>
              </Box>
            )}
            <Box sx={{ flex: "1 1 320px", minWidth: 260 }}>
              <RadioGroup row value={sectionFilter} onChange={(e) => setSectionFilter(e.target.value as "all" | "root" | "child")} sx={{ mb: 1 }}>
                <FormControlLabel value="all" control={<Radio size="small" />} label={<Typography variant="body2">Todas</Typography>} />
                <FormControlLabel value="root" control={<Radio size="small" />} label={<Typography variant="body2">Mães</Typography>} />
                <FormControlLabel value="child" control={<Radio size="small" />} label={<Typography variant="body2">Filhas</Typography>} />
              </RadioGroup>
              {modalSections.length === 0 ? (
                <Typography variant="body2" sx={{ color: colors.textSecondary }}>
                  Nenhuma seção{sectionFilter !== "all" ? " neste filtro" : ""}. Elas agrupam os itens (ex.: áreas do desenvolvimento) e podem ter cálculo e faixas de resultado próprios.
                </Typography>
              ) : (
                <Box sx={{ maxHeight: 420, overflowY: "auto", display: "flex", flexDirection: "column", gap: 1, pr: 0.5 }}>
                  {modalSections.map((section) => (
                    <Box key={section.id} sx={{ border: "1px solid #e9ecef", borderRadius: "8px", p: 1, backgroundColor: sectionDraft?.id === section.id ? "#e0f5f7" : "#fff" }}>
                      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography sx={{ fontWeight: 600, color: colors.textPrimary, wordBreak: "break-word", fontSize: "0.9rem" }}>
                            {section.ordenation}. {section.title}
                          </Typography>
                          <Typography variant="caption" sx={{ color: colors.textSecondary, display: "block" }}>
                            {section.parentSectionId ? `Mãe: ${sectionsById.get(section.parentSectionId)?.title ?? "?"} · ` : "Nível 1 · "}
                            {section.questionsCount} item{section.questionsCount === 1 ? "" : "s"}
                            {section.calcTypeId ? ` · Resultado: ${calcTypeName(section.calcTypeId) ?? "?"}` : ""}
                          </Typography>
                        </Box>
                        {!locked && (
                          <Box sx={{ display: "flex", gap: 0.5, flexShrink: 0 }}>
                            <IconButton size="small" onClick={() => setSectionDraft({ id: section.id, title: section.title, description: section.description ?? "", interpretationGuide: section.interpretationGuide ?? "", parentSectionId: section.parentSectionId ?? NONE, ordenation: String(section.ordenation), calcTypeId: section.calcTypeId ?? NONE })} sx={{ color: colors.primary }}>
                              <Edit sx={{ fontSize: "1rem" }} />
                            </IconButton>
                            <IconButton size="small" onClick={() => setSectionToDelete(section)} sx={{ color: colors.textSecondary, "&:hover": { color: "#dc3545" } }}>
                              <DeleteOutline sx={{ fontSize: "1rem" }} />
                            </IconButton>
                          </Box>
                        )}
                      </Box>
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Modal de item (opções inline, estilo Google Forms) */}
      <Dialog open={questionDraft !== null} onClose={() => setQuestionDraft(null)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: "12px", position: "relative", overflow: "hidden" } }}>
        <SavingOverlay open={saving} message="Salvando..." />
        <DialogTitle sx={{ backgroundColor: colors.primary, color: colors.white, display: "flex", justifyContent: "space-between", alignItems: "center", padding: "1rem 1.25rem" }}>
          <Typography component="h3" sx={{ fontSize: "1.1rem", fontWeight: typography.fontWeight.semibold, margin: 0 }}>
            {locked ? "Item (somente leitura)" : questionDraft?.id ? "Editar item" : "Novo item"}
          </Typography>
          <IconButton onClick={() => setQuestionDraft(null)} sx={{ color: colors.white, padding: "0.25rem" }}><Close /></IconButton>
        </DialogTitle>
        {questionDraft && (
          <DialogContent sx={{ padding: "1.25rem !important", paddingTop: "1.5rem !important", display: "flex", flexDirection: "column", gap: 2 }}>
            <TextField label="Item *" size="small" disabled={locked} value={questionDraft.text} onChange={(e) => setQuestionDraft({ ...questionDraft, text: e.target.value })} InputLabelProps={{ shrink: true }} inputProps={{ maxLength: 1000 }} multiline minRows={2} fullWidth />
            <TextField label="Descrição" size="small" disabled={locked} value={questionDraft.description} onChange={(e) => setQuestionDraft({ ...questionDraft, description: e.target.value })} InputLabelProps={{ shrink: true }} inputProps={{ maxLength: 1000 }} fullWidth />
            {/* Seção ocupa a linha inteira: o caminho "Mãe > Filha" nao cabe em coluna estreita */}
            <FormControl size="small" fullWidth>
              <InputLabel shrink>Seção</InputLabel>
              <Select label="Seção" disabled={locked} value={questionDraft.sectionId} onChange={(e) => setQuestionDraft({ ...questionDraft, sectionId: e.target.value })} notched displayEmpty>
                <MenuItem value={NONE}>Sem seção</MenuItem>
                {orderedSections.map((s) => (
                  <MenuItem key={s.id} value={s.id}>{sectionLabel(s.id)}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
              <FormControl size="small" sx={{ flex: "1 1 170px" }}>
                <InputLabel shrink>Tipo *</InputLabel>
                <Select label="Tipo *" disabled={locked} value={questionDraft.questionType} onChange={(e) => setQuestionDraft({ ...questionDraft, questionType: e.target.value })} notched>
                  {(editor?.catalogs.questionTypes ?? []).map((t) => (
                    <MenuItem key={t} value={t}>{QUESTION_TYPE_LABELS[t] ?? t}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField label="Ordenação" size="small" disabled={locked} type="number" value={questionDraft.ordenation} onChange={(e) => setQuestionDraft({ ...questionDraft, ordenation: e.target.value })} InputLabelProps={{ shrink: true }} inputProps={{ min: 0, max: 99999 }} sx={{ flex: "0 1 110px" }} />
            </Box>

            {isChoice(questionDraft.questionType) && (
              <Box sx={{ border: "1px solid #e9ecef", borderRadius: "8px", p: 1.25 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, color: colors.textPrimary, mb: 1 }}>
                  Opções de resposta (o valor pontua o resultado; vazio = sem pontuação)
                </Typography>
                {questionDraft.options.map((option, index) => (
                  <Box key={index} sx={{ display: "flex", gap: 1, alignItems: "center", mb: 1 }}>
                    <TextField size="small" disabled={locked} placeholder={`Opção ${index + 1}`} value={option.label} onChange={(e) => { const options = [...questionDraft.options]; options[index] = { ...option, label: e.target.value }; setQuestionDraft({ ...questionDraft, options }); }} inputProps={{ maxLength: 255 }} sx={{ flex: 1 }} />
                    <TextField size="small" disabled={locked} placeholder="Valor" type="number" value={option.value} onChange={(e) => { const options = [...questionDraft.options]; options[index] = { ...option, value: e.target.value }; setQuestionDraft({ ...questionDraft, options }); }} inputProps={{ step: "0.5" }} sx={{ width: 90 }} />
                    {!locked && (
                      <>
                        <IconButton size="small" disabled={index === 0} onClick={() => moveOption(index, -1)} sx={{ color: colors.textSecondary }}><ArrowUpward sx={{ fontSize: "0.9rem" }} /></IconButton>
                        <IconButton size="small" disabled={index === questionDraft.options.length - 1} onClick={() => moveOption(index, 1)} sx={{ color: colors.textSecondary }}><ArrowDownward sx={{ fontSize: "0.9rem" }} /></IconButton>
                        <IconButton size="small" onClick={() => setQuestionDraft({ ...questionDraft, options: questionDraft.options.filter((_, i) => i !== index) })} sx={{ color: colors.textSecondary, "&:hover": { color: "#dc3545" } }}><DeleteOutline sx={{ fontSize: "0.95rem" }} /></IconButton>
                      </>
                    )}
                  </Box>
                ))}
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 1 }}>
                  {!locked && (
                  <Button size="small" startIcon={<Add sx={{ fontSize: "0.9rem" }} />} onClick={() => setQuestionDraft({ ...questionDraft, options: [...questionDraft.options, { id: null, label: "", value: "" }] })} sx={{ textTransform: "none", color: colors.primary }}>
                    Opção
                  </Button>
                  )}
                  {!locked && questionDraft.id && questionDraft.sectionId && (
                    <Tooltip title="Substitui as opções das demais itens de escolha desta seção pelas opções desto item (use após salvar)." arrow>
                      <Button size="small" onClick={applyOptionsToSection} disabled={saving} sx={{ textTransform: "none", color: colors.primary }}>
                        Aplicar às demais da seção
                      </Button>
                    </Tooltip>
                  )}
                </Box>
              </Box>
            )}

            <Box sx={{ display: "flex", flexDirection: "column" }}>
              <FormControlLabel control={<Checkbox size="small" disabled={locked} checked={questionDraft.required} onChange={(e) => setQuestionDraft({ ...questionDraft, required: e.target.checked })} />} label={<Typography variant="body2">Resposta obrigatória</Typography>} />
              <FormControlLabel control={<Checkbox size="small" disabled={locked} checked={questionDraft.allowComment} onChange={(e) => setQuestionDraft({ ...questionDraft, allowComment: e.target.checked, commentRequired: e.target.checked ? questionDraft.commentRequired : false })} />} label={<Typography variant="body2">Exibir campo de comentário</Typography>} />
              <FormControlLabel control={<Checkbox size="small" checked={questionDraft.commentRequired} disabled={locked || !questionDraft.allowComment} onChange={(e) => setQuestionDraft({ ...questionDraft, commentRequired: e.target.checked })} />} label={<Typography variant="body2">Comentário obrigatório</Typography>} />
            </Box>
            {questionDraft.questionType === "multiple_choice" && (
              <Box sx={{ display: "flex", gap: 1.5 }}>
                <TextField label="Mínimo de escolhas" size="small" disabled={locked} type="number" value={questionDraft.minChoices} onChange={(e) => setQuestionDraft({ ...questionDraft, minChoices: e.target.value })} InputLabelProps={{ shrink: true }} inputProps={{ min: 0, max: 999 }} sx={{ flex: 1 }} />
                <TextField label="Máximo de escolhas" size="small" disabled={locked} type="number" value={questionDraft.maxChoices} onChange={(e) => setQuestionDraft({ ...questionDraft, maxChoices: e.target.value })} InputLabelProps={{ shrink: true }} inputProps={{ min: 0, max: 999 }} sx={{ flex: 1 }} />
              </Box>
            )}
          </DialogContent>
        )}
        <DialogActions sx={{ padding: "1rem 1.25rem" }}>
          <Button onClick={() => setQuestionDraft(null)} variant="outlined" sx={{ textTransform: "none", color: "#6c757d", borderColor: "#6c757d" }}>{locked ? "Fechar" : "Cancelar"}</Button>
          {!locked && (
            <Button variant="contained" disabled={!questionDraft?.text.trim() || saving} onClick={saveQuestion} sx={{ textTransform: "none", backgroundColor: colors.primary }}>Salvar</Button>
          )}
        </DialogActions>
      </Dialog>

      <ConfirmModal
        isOpen={sectionToDelete !== null}
        title="Excluir seção"
        message={`Excluir a seção "${sectionToDelete?.title ?? ""}"? Só é possível quando não há itens nem subseções vinculadas; as faixas da seção saem junto.`}
        type="warning"
        confirmButtonText="Excluir"
        cancelButtonText="Cancelar"
        isProcessing={saving}
        onConfirm={async () => {
          if (!sectionToDelete) return;
          const ok = await run(() => assessmentFormsService.deleteSection(sectionToDelete.id), "Seção excluída.", "Erro ao excluir a seção.");
          if (ok) {
            if (sectionDraft?.id === sectionToDelete.id) setSectionDraft(emptySectionDraft());
            setSectionToDelete(null);
          }
        }}
        onCancel={() => setSectionToDelete(null)}
      />

      <ConfirmModal
        isOpen={questionToDelete !== null}
        title="Excluir item"
        message={`Excluir o item "${(questionToDelete?.text ?? "").slice(0, 80)}${(questionToDelete?.text?.length ?? 0) > 80 ? "…" : ""}"? As opções dela saem junto.`}
        type="warning"
        confirmButtonText="Excluir"
        cancelButtonText="Cancelar"
        isProcessing={saving}
        onConfirm={async () => {
          if (!questionToDelete) return;
          const ok = await run(() => assessmentFormsService.deleteQuestion(questionToDelete.id), "Item excluída.", "Erro ao excluir o item.");
          if (ok) setQuestionToDelete(null);
        }}
        onCancel={() => setQuestionToDelete(null)}
      />

      <Toast message={toast.message} type={toast.type} isVisible={toast.isVisible} onClose={hideToast} />
      <FooterInternal className="login-footer-component" />
    </Box>
  );
};

export default AssessmentFormEditor;
